/**
 * Services — library surfaces. Verbatim port of the canonical
 * screens-services.jsx (Services / Categories / Packages tabs).
 *
 *   • Services   (route: services)            — list/card library + filters
 *   • Categories (route: services.categories) — drag-and-drop board
 *   • Packages   (route: services.packages)   — bundle library
 *
 * Every entity is Published / Draft. Editors (services.edit /
 * services.category.edit / services.package.edit) port in the next pass.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Card, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { StatTile, FilterBar, EmptyState, ExplainerFooter, FilterDropdown, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { useNav, type NavApi } from "../../shell/router";
import { toast } from "../../shell/toast";
import {
  SVC_CHANNELS, SVC_CHANNEL, SVC_MODES, useServices, ServicesStore,
  svcCategoryName, svcCategoryHue, svcDurationLabel, type Service, type SvcCategory, type SvcPackage,
} from "./services-data";
import { useT, t as i18nT } from "../../lib/i18n";

const IconMap = Icon as unknown as Record<string, (p?: { size?: number; style?: CSSProperties }) => ReactNode>;
const svcTh: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" };
const svcTd: CSSProperties = { padding: "12px 16px", verticalAlign: "middle", color: T.n2 };

export const svcStatusPill = (status: string) =>
  status === "published" ? <Pill tone="success" dot>{i18nT("common.status.published", "Published")}</Pill> : <Pill tone="warn" dot>{i18nT("common.status.draft", "Draft")}</Pill>;

export function SvcChannelGlyph({ id, size = 13, color }: { id: string; size?: number; color?: string }) {
  const c = SVC_CHANNEL(id);
  const Glyph = IconMap[c.icon] || IconMap.calendar;
  return <>{Glyph({ size, style: { color: color || c.hue } })}</>;
}
export function SvcChannelChip({ id }: { id: string }) {
  const c = SVC_CHANNEL(id);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px 3px 7px", borderRadius: 999, background: c.hue + "14", color: c.hue, border: `1px solid ${c.hue}33`, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>
      <SvcChannelGlyph id={id} size={12} /> {c.label}
    </span>
  );
}
export function SvcModePill({ mode }: { mode: string }) {
  const m = SVC_MODES[mode] || SVC_MODES["in-person"];
  return <Pill tone={m.tone as any}>{m.label}</Pill>;
}

/* Shared status filter-chip row used on every Services tab. */
function SvcStatusChips({ value, onChange, counts }: { value: string; onChange: (v: string) => void; counts: { all: number; published: number } }) {
  const items: [string, string][] = [["all", `All · ${counts.all}`], ["published", `Published · ${counts.published}`], ["draft", `Drafts · ${counts.all - counts.published}`]];
  return <>{items.map(([id, lbl]) => (
    <button key={id} type="button" onClick={() => onChange(id)} style={{ padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 500, background: value === id ? T.p1 : T.white, color: value === id ? T.white : T.n1, border: `1px solid ${value === id ? T.p1 : T.borderStrong}`, cursor: "pointer", fontFamily: T.font }}>{lbl}</button>
  ))}</>;
}

/* Main section tab strip — identical on every Services screen. */
export function servicesSectionTabs(nav: NavApi) {
  const s = ServicesStore;
  return [
    { id: "services",   label: i18nT("services.screens.services", "Services"),   count: s.services().length,   onClick: () => nav.go("services") },
    { id: "categories", label: i18nT("services.screens.categories", "Categories"), count: s.categories().length, onClick: () => nav.go("services.categories") },
    { id: "packages",   label: i18nT("services.screens.packages", "Packages"),   count: s.packages().length,   onClick: () => nav.go("services.packages") },
  ];
}

/* ═══ Tab 1 · Services ════════════════════════════════════════════════════ */
export default function ServicesLibrary() {
  const t = useT();
  const nav = useNav();
  const store = useServices();
  const [view, setView] = useViewMode("services:view");
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState<string | null>(null);
  const [chanFilter, setChanFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const canWrite = can("services.write");

  const services = store.services();
  const categories = store.categories();
  const rows = services.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (catFilter && svcCategoryName(s.category) !== catFilter) return false;
    if (chanFilter && !s.channels.includes(chanFilter)) return false;
    if (query.trim() && !`${s.name} ${svcCategoryName(s.category)}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const published = services.filter((s) => s.status === "published").length;

  const dup = async (s: Service) => {
    try {
      const c = await store.duplicateService(s.id);
      toast({ tone: "success", title: t("services.screens.serviceDuplicated", "Service duplicated"), subtitle: c ? `"${c.name}" added as a draft.` : undefined });
    } catch (e) {
      toast({ tone: "danger", title: t("services.screens.couldnTDuplicateService", "Couldn't duplicate service"), subtitle: t("services.screens.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = (s: Service) => { store.removeService(s.id); toast({ tone: "warn", title: t("services.screens.serviceDeleted", "Service deleted") }); };
  const toggle = (s: Service) => { const next = s.status === "published" ? "draft" : "published"; store.setServiceStatus(s.id, next); toast({ tone: next === "published" ? "success" : "warn", title: next === "published" ? "Service published" : "Moved to draft" }); };
  const actions = (s: Service) => ({
    onView: () => nav.go("services.edit", { id: s.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("services.edit", { id: s.id }) : undefined,
    onDuplicate: canWrite ? () => dup(s) : undefined,
    moreHeader: s.name,
    moreItems: canWrite ? [
      s.status === "published"
        ? { icon: <Icon.refresh size={14} />, label: t("services.screens.moveToDraft", "Move to draft"), onClick: () => toggle(s) }
        : { icon: <Icon.check size={14} />, label: t("common.actions.publish", "Publish"), onClick: () => toggle(s) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("services.screens.deleteService", "Delete service"), danger: true, onClick: () => del(s) },
    ] : [],
  });

  const catNames = [...new Set(services.map((s) => svcCategoryName(s.category)))];
  const totalBookings = services.reduce((n, s) => n + (s.bookings || 0), 0);
  const anyFilter = catFilter || chanFilter || query.trim() || statusFilter !== "all";

  return (
    <Screen rail="cs-services" tabs={servicesSectionTabs(nav)} activeTab="services">
      <PageHeader
        title={t("services.screens.services", "Services")}
        subtitle={t("services.screens.reusableBookableOfferingsSurfaceEachOneThroughEventsAppointmentsAndQueue", "Reusable, bookable offerings — surface each one through events, appointments and queues.")}
        breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("services.edit", { new: 1 })}>{t("services.screens.newService", "New service")}</Btn> : null}
        </>}
      />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("services.screens.services", "Services")} value={services.length} icon={<Icon.layers size={14} />} accent={T.p1} />
        <StatTile label={t("common.status.published", "Published")} value={published} icon={<Icon.check size={14} />} accent={T.green1} />
        <StatTile label={t("services.screens.categories", "Categories")} value={categories.length} icon={<Icon.tags size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("services.screens.totalBookings", "Total bookings")} value={totalBookings.toLocaleString()} icon={<Icon.users size={14} />} accent={T.p1} />
      </div>

      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("services.screens.searchServices", "Search services…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <SvcStatusChips value={statusFilter} onChange={setStatusFilter} counts={{ all: services.length, published }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <FilterDropdown label={catFilter || t("services.servicesScreens.anyCategory", "Any category")} active={!!catFilter} options={catNames} onPick={setCatFilter} onClear={() => setCatFilter(null)} />
        <FilterDropdown label={chanFilter ? SVC_CHANNEL(chanFilter).label : t("services.servicesScreens.anyChannel", "Any channel")} active={!!chanFilter}
          options={SVC_CHANNELS.map((c) => c.label)}
          onPick={(label) => setChanFilter((SVC_CHANNELS.find((c) => c.label === label) || SVC_CHANNELS[0]).id)}
          onClear={() => setChanFilter(null)} />
        {anyFilter ? <button onClick={() => { setCatFilter(null); setChanFilter(null); setQuery(""); setStatusFilter("all"); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t("services.screens.clearAll", "Clear all")}</button> : null}
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{rows.length} {t("services.screens.of", "of")}{" "}{services.length}</span>
      </FilterBar>

      <div style={{ padding: "0 24px" }}>
        <ExplainerFooter>
          <b>{t("services.screens.services", "Services")}</b> {t("services.screens.areReusableOfferingsYouCanAddTo", "are reusable offerings you can add to")}{" "}<b>{t("services.screens.events", "events")}</b>{t("services.screens.bookAs", ", book as")}{" "}<b>{t("services.screens.appointments", "appointments")}</b>{t("services.screens.orRunThrough", ", or run through")}{" "}<b>{t("services.screens.queues", "queues")}</b>{t("services.screens.groupThemOnThe", ". Group them on the")}{" "}<b>{t("services.screens.categories", "Categories")}</b> {t("services.screens.tabThenBundleCategoriesAndServicesIntoA", "tab, then bundle categories and services into a")}{" "}<b>{t("services.screens.package", "Package")}</b>.
        </ExplainerFooter>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {rows.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.layers size={20} />} title={t("services.screens.noServicesMatch", "No services match")} body={t("services.screens.tryClearingAFilterOrCreateANewService", "Try clearing a filter, or create a new service.")}
            action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("services.edit", { new: 1 })}>{t("services.screens.newService", "New service")}</Btn> : null} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
            {rows.map((s) => {
              const hue = svcCategoryHue(s.category);
              return (
                <Card key={s.id} onClick={() => actions(s).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column", borderLeft: `3px solid ${s.status === "published" ? T.green1 : T.orange1}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = T.shadowMd)} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}>
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <span style={{ width: 32, height: 32, borderRadius: 7, background: hue + "1f", color: hue, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={16} /></span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{svcCategoryName(s.category)}</div>
                      </div>
                      {svcStatusPill(s.status)}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <SvcModePill mode={s.mode} />
                      <span style={{ fontSize: 12, color: T.soft, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.clock size={12} /> {svcDurationLabel(s.duration)}</span>
                      {s.capacity > 1 ? <span style={{ fontSize: 12, color: T.soft, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.users size={12} /> {s.capacity}</span> : null}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{s.channels.map((id: string) => <SvcChannelChip key={id} id={id} />)}</div>
                  </div>
                  <CardActionBar left={<span><strong style={{ color: T.n1 }}>{(s.bookings || 0).toLocaleString()}</strong> {t("services.screens.bookings08c8fe05", "bookings ·")}{s.staff} {t("services.screens.staff", "staff")}</span>} {...actions(s)} />
                </Card>
              );
            })}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={svcTh}>{t("services.screens.service", "Service")}</th><th style={svcTh}>{t("services.screens.category", "Category")}</th><th style={svcTh}>{t("services.screens.mode", "Mode")}</th><th style={svcTh}>{t("services.screens.duration", "Duration")}</th><th style={svcTh}>{t("services.screens.channels", "Channels")}</th><th style={svcTh}>{t("services.screens.bookings", "Bookings")}</th><th style={svcTh}>{t("services.screens.status", "Status")}</th><th style={{ ...svcTh, width: 140, textAlign: "right" }}>{t("services.screens.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s, i) => (
                  <tr key={s.id} onClick={() => actions(s).onView()} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", borderLeft: `3px solid ${s.status === "published" ? T.green1 : T.orange1}` }}>
                    <td style={svcTd}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 30, height: 30, borderRadius: 7, background: svcCategoryHue(s.category) + "1f", color: svcCategoryHue(s.category), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={15} /></span>
                        <div style={{ color: T.n1, fontWeight: 600 }}>{s.name}</div>
                      </div>
                    </td>
                    <td style={svcTd}>{svcCategoryName(s.category)}</td>
                    <td style={svcTd}><SvcModePill mode={s.mode} /></td>
                    <td style={svcTd}>{svcDurationLabel(s.duration)}</td>
                    <td style={svcTd}><div style={{ display: "flex", gap: 5 }}>{s.channels.map((id: string) => <span key={id} title={SVC_CHANNEL(id).label} style={{ display: "inline-flex" }}><SvcChannelGlyph id={id} size={15} /></span>)}</div></td>
                    <td style={{ ...svcTd, color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{(s.bookings || 0).toLocaleString()}</td>
                    <td style={svcTd}>{svcStatusPill(s.status)}</td>
                    <td style={svcTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(s)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Screen>
  );
}

/* ═══ Tab 2 · Categories — drag-and-drop board ════════════════════════════ */
export function ServiceCategories() {
  const t = useT();
  const nav = useNav();
  const store = useServices();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const canWrite = can("services.write");
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCat, setOverCat] = useState<string | null>(null);
  const [overSvc, setOverSvc] = useState<string | null>(null);

  const categories = store.categories();
  const published = categories.filter((c) => c.status === "published").length;
  const shown = categories.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (query.trim() && !c.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const orphans = store.services().filter((s) => !s.category || !store.getCategory(s.category));

  const dup = async (c: SvcCategory) => {
    try {
      const n = await store.duplicateCategory(c.id);
      toast({ tone: "success", title: t("services.screens.categoryDuplicated", "Category duplicated"), subtitle: n ? `"${n.name}" added as a draft.` : undefined });
    } catch (e) {
      toast({ tone: "danger", title: t("services.screens.couldnTDuplicateCategory", "Couldn't duplicate category"), subtitle: t("services.screens.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = (c: SvcCategory) => { store.removeCategory(c.id); toast({ tone: "warn", title: t("services.screens.categoryDeleted", "Category deleted"), subtitle: t("services.screens.itsServicesWereMovedToUncategorised", "Its services were moved to Uncategorised.") }); };
  const toggle = (c: SvcCategory) => { const next = c.status === "published" ? "draft" : "published"; store.setCategoryStatus(c.id, next); toast({ tone: next === "published" ? "success" : "warn", title: next === "published" ? "Category published" : "Moved to draft" }); };

  const onDrop = (catId: string | null, beforeId: string | null) => {
    if (dragId) store.moveService(dragId, catId as string, beforeId || null);
    setDragId(null); setOverCat(null); setOverSvc(null);
  };

  const ServiceRow = ({ s, catId }: { s: Service; catId: string | null }) => (
    <div
      draggable={canWrite}
      onDragStart={(e) => { setDragId(s.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => { setDragId(null); setOverCat(null); setOverSvc(null); }}
      onDragOver={(e) => { if (dragId && dragId !== s.id) { e.preventDefault(); e.stopPropagation(); setOverCat(catId); setOverSvc(s.id); } }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(catId, s.id); }}
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: 8,
        background: T.white, border: `1px solid ${overSvc === s.id ? T.p1 : T.border}`,
        boxShadow: overSvc === s.id ? `0 -2px 0 ${T.p1}` : "none",
        cursor: canWrite ? "grab" : "default", opacity: dragId === s.id ? 0.4 : 1,
      }}>
      {canWrite ? <Icon.drag size={14} style={{ color: T.n3, flexShrink: 0 }} /> : null}
      <span style={{ width: 26, height: 26, borderRadius: 6, background: svcCategoryHue(catId) + "1f", color: svcCategoryHue(catId), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={13} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
        <div style={{ fontSize: 11, color: T.soft }}>{(SVC_MODES[s.mode] || SVC_MODES["in-person"]).label} · {svcDurationLabel(s.duration)}</div>
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>{s.channels.map((id: string) => <span key={id} title={SVC_CHANNEL(id).label}><SvcChannelGlyph id={id} size={13} /></span>)}</div>
      {svcStatusPill(s.status)}
    </div>
  );

  const CategoryColumn = ({ c }: { c: SvcCategory }) => {
    const items = store.servicesIn(c.id);
    const isOver = overCat === c.id;
    return (
      <Card style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", borderTop: `4px solid ${c.hue}`, outline: isOver && !overSvc ? `2px solid ${T.p1}` : "none" }}>
        <div style={{ padding: "13px 15px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 12, height: 12, borderRadius: 4, background: c.hue, flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{c.name}</div>
            <div style={{ fontSize: 11.5, color: T.soft }}>{items.length} {t("services.screens.service9df6b026", "service")}{" "}{items.length === 1 ? "" : t('services.screens.text')}</div>
          </div>
          {svcStatusPill(c.status)}
          {canWrite ? <RowActions
            onEdit={() => nav.go("services.category.edit", { id: c.id })}
            onDuplicate={() => dup(c)}
            moreHeader={c.name}
            moreItems={[
              { icon: <Icon.arrow dir="up" size={14} />, label: t("services.screens.moveUp", "Move up"), onClick: () => store.reorderCategory(c.id, "up") },
              { icon: <Icon.arrow dir="down" size={14} />, label: t("services.screens.moveDown", "Move down"), onClick: () => store.reorderCategory(c.id, "down") },
              c.status === "published"
                ? { icon: <Icon.refresh size={14} />, label: t("services.screens.moveToDraft", "Move to draft"), onClick: () => toggle(c) }
                : { icon: <Icon.check size={14} />, label: t("common.actions.publish", "Publish"), onClick: () => toggle(c) },
              "divider" as const,
              { icon: <Icon.trash size={14} />, label: t("services.screens.deleteCategory", "Delete category"), danger: true, onClick: () => del(c) },
            ]} /> : null}
        </div>
        <div
          onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverCat(c.id); setOverSvc(null); } }}
          onDrop={(e) => { e.preventDefault(); onDrop(c.id, null); }}
          style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8, flex: 1, minHeight: 90, background: isOver ? T.p4 + "55" : "transparent" }}>
          {items.length === 0
            ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.soft, fontSize: 12.5, border: `1.5px dashed ${T.border}`, borderRadius: 8, padding: 18, fontStyle: "italic" }}>{t("services.screens.dragServicesHere", "Drag services here")}</div>
            : items.map((s) => <ServiceRow key={s.id} s={s} catId={c.id} />)}
        </div>
      </Card>
    );
  };

  return (
    <Screen rail="cs-services" tabs={servicesSectionTabs(nav)} activeTab="categories">
      <PageHeader
        title={t("services.screens.categories", "Categories")}
        subtitle={t("services.screens.groupYourServicesDragAServiceFromOneCategoryToAnotherOr", "Group your services. Drag a service from one category to another, or reorder within a category.")}
        breadcrumb={null}
        actions={canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("services.category.edit", { new: 1 })}>{t("services.screens.newCategory", "New category")}</Btn> : null}
      />
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("services.screens.searchCategories", "Search categories…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <SvcStatusChips value={statusFilter} onChange={setStatusFilter} counts={{ all: categories.length, published }} />
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{shown.length} {t("services.screens.of", "of")}{" "}{categories.length}</span>
      </FilterBar>

      <div style={{ padding: "0 24px" }}>
        <ExplainerFooter>
          {t("services.screens.categoriesKeepYourServicesOrganisedAndPower", "Categories keep your services organised and power")}{" "}<b>{t("services.screens.packages", "Packages")}</b> {t("services.screens.whenAPackageIncludesAWholeCategoryItAutomaticallyPicksUpEvery", "— when a package includes a whole category, it automatically picks up every service you drag into it here.")}</ExplainerFooter>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {canWrite ? <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.soft, padding: "0 0 12px" }}><Icon.drag size={14} /> {t("services.screens.dragTheHandleOnAnyServiceToMoveItBetweenCategoriesOr", "Drag the handle on any service to move it between categories or reorder it.")}</div> : null}
        {shown.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.tags size={20} />} title={t("services.screens.noCategoriesMatch", "No categories match")} body={t("services.screens.tryADifferentSearchOrCreateANewCategory", "Try a different search, or create a new category.")}
            action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("services.category.edit", { new: 1 })}>{t("services.screens.newCategory", "New category")}</Btn> : null} />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 16, alignItems: "start" }}>
            {shown.map((c) => <CategoryColumn key={c.id} c={c} />)}
            {orphans.length ? (
              <Card style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", borderTop: `4px solid ${T.n4}`, outline: overCat === "__none" && !overSvc ? `2px solid ${T.p1}` : "none" }}>
                <div style={{ padding: "13px 15px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 4, background: T.n4, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}><div style={{ fontSize: 14.5, fontWeight: 700, color: T.n2 }}>{t("services.screens.uncategorised", "Uncategorised")}</div><div style={{ fontSize: 11.5, color: T.soft }}>{orphans.length} {t("services.screens.service9df6b026", "service")}{" "}{orphans.length === 1 ? "" : t('services.screens.text')}</div></div>
                </div>
                <div onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverCat("__none"); setOverSvc(null); } }} onDrop={(e) => { e.preventDefault(); if (dragId) { store.moveService(dragId, null as unknown as string, null); setDragId(null); setOverCat(null); } }}
                  style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8, flex: 1, minHeight: 70 }}>
                  {orphans.map((s) => <ServiceRow key={s.id} s={s} catId={null} />)}
                </div>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </Screen>
  );
}

/* ═══ Tab 3 · Packages — list/card library ════════════════════════════════ */
export function ServicePackages() {
  const t = useT();
  const nav = useNav();
  const store = useServices();
  const [view, setView] = useViewMode("servicepackages:view");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const canWrite = can("services.write");

  const all = store.packages();
  const rows = all.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (query.trim() && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const published = all.filter((p) => p.status === "published").length;
  const dup = async (p: SvcPackage) => {
    try {
      const c = await store.duplicatePackage(p.id);
      toast({ tone: "success", title: t("services.screens.packageDuplicated", "Package duplicated"), subtitle: c ? `"${c.name}" added as a draft.` : undefined });
    } catch (e) {
      toast({ tone: "danger", title: t("services.screens.couldnTDuplicatePackage", "Couldn't duplicate package"), subtitle: t("services.screens.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = (p: SvcPackage) => { store.removePackage(p.id); toast({ tone: "warn", title: t("services.screens.packageDeleted", "Package deleted") }); };
  const toggle = (p: SvcPackage) => { const next = p.status === "published" ? "draft" : "published"; store.setPackageStatus(p.id, next); toast({ tone: next === "published" ? "success" : "warn", title: next === "published" ? "Package published" : "Moved to draft" }); };
  const actions = (p: SvcPackage) => ({
    onView: () => nav.go("services.package.edit", { id: p.id }),
    viewLabel: "Open",
    onEdit: canWrite ? () => nav.go("services.package.edit", { id: p.id }) : undefined,
    onDuplicate: canWrite ? () => dup(p) : undefined,
    moreHeader: p.name,
    moreItems: canWrite ? [
      p.status === "published"
        ? { icon: <Icon.refresh size={14} />, label: t("services.screens.moveToDraft", "Move to draft"), onClick: () => toggle(p) }
        : { icon: <Icon.check size={14} />, label: t("common.actions.publish", "Publish"), onClick: () => toggle(p) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("services.screens.deletePackage", "Delete package"), danger: true, onClick: () => del(p) },
    ] : [],
  });

  const itemSummary = (p: SvcPackage) => {
    const cats = p.items.filter((i) => i.type === "category").length;
    const svcs = p.items.filter((i) => i.type === "service").length;
    const parts: string[] = [];
    if (cats) parts.push(`${cats} categor${cats === 1 ? "y" : "ies"}`);
    if (svcs) parts.push(`${svcs} service${svcs === 1 ? "" : "s"}`);
    return parts.join(" · ") || "Empty";
  };

  return (
    <Screen rail="cs-services" tabs={servicesSectionTabs(nav)} activeTab="packages">
      <PageHeader
        title={t("services.screens.packages", "Packages")}
        subtitle={t("services.screens.bundleWholeCategoriesAndIndividualServicesIntoASingleOfferingBuildThem", "Bundle whole categories and individual services into a single offering. Build them by drag-and-drop.")}
        breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("services.package.edit", { new: 1 })}>{t("services.screens.newPackage", "New package")}</Btn> : null}
        </>}
      />
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("services.screens.searchPackages", "Search packages…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <SvcStatusChips value={statusFilter} onChange={setStatusFilter} counts={{ all: all.length, published }} />
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{rows.length} {t("services.screens.of", "of")}{" "}{all.length}</span>
      </FilterBar>

      <div style={{ padding: "0 24px" }}>
        <ExplainerFooter>
          <b>{t("services.screens.packages", "Packages")}</b> {t("services.screens.bundleWhole", "bundle whole")}{" "}<b>{t("services.screens.categoriesa6216ea0", "categories")}</b> {t("services.screens.andIndividual", "and individual")}{" "}<b>{t("services.screens.servicesef1c4b45", "services")}</b> {t("services.screens.intoOneOfferingPickACategoryToIncludeEverythingInItThen", "into one offering — pick a category to include everything in it, then drop in a few extra services. Build by drag-and-drop.")}</ExplainerFooter>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {rows.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.package size={20} />} title={t("services.screens.noPackagesMatch", "No packages match")} body={t("services.screens.tryClearingAFilterOrBuildANewPackage", "Try clearing a filter, or build a new package.")}
            action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("services.package.edit", { new: 1 })}>{t("services.screens.newPackage", "New package")}</Btn> : null} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
            {rows.map((p) => {
              const count = store.packageServiceCount(p);
              return (
                <Card key={p.id} onClick={() => actions(p).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column" }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = T.shadowMd)} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}>
                  <div style={{ height: 92, background: `linear-gradient(135deg, ${p.hue}, ${T.p1})`, padding: 14, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.white, fontSize: 12, fontWeight: 600, background: "rgba(255,255,255,0.2)", padding: "3px 9px", borderRadius: 999 }}><Icon.package size={13} /> {itemSummary(p)}</span>
                      {p.status === "draft" ? svcStatusPill(p.status) : null}
                    </div>
                    <div style={{ color: T.white, fontWeight: 700, fontSize: 18, lineHeight: 1.2, letterSpacing: -0.3 }}>{p.name}</div>
                  </div>
                  <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                    <p style={{ fontSize: 13, color: T.soft, margin: 0, lineHeight: 1.5 }}>{p.description}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {p.items.slice(0, 4).map((it, idx) => it.type === "category"
                        ? <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: svcCategoryHue(it.id) + "1a", color: svcCategoryHue(it.id) }}><Icon.tags size={11} /> {svcCategoryName(it.id)}</span>
                        : <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: T.n6, color: T.n2 }}><Icon.layers size={11} /> {(store.getService(it.id) || ({} as Service)).name || "—"}</span>)}
                      {p.items.length > 4 ? <span style={{ fontSize: 11.5, color: T.soft, alignSelf: "center" }}>+{p.items.length - 4}</span> : null}
                    </div>
                  </div>
                  <CardActionBar left={<span><strong style={{ color: T.n1 }}>{count}</strong> {t("services.screens.servicesEdited", "services · edited")}{" "}{p.updated}</span>} {...actions(p)} />
                </Card>
              );
            })}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={svcTh}>{t("services.screens.package", "Package")}</th><th style={svcTh}>{t("services.screens.contents", "Contents")}</th><th style={svcTh}>{t("services.screens.services", "Services")}</th><th style={svcTh}>{t("services.screens.status", "Status")}</th><th style={svcTh}>{t("services.screens.updated", "Updated")}</th><th style={{ ...svcTh, width: 140, textAlign: "right" }}>{t("services.screens.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr key={p.id} onClick={() => actions(p).onView()} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                    <td style={svcTd}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {canWrite ? (
                          <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <button type="button" disabled={i === 0} onClick={() => store.reorderPackage(p.id, "up")} title={t("services.screens.moveUp", "Move up")} style={{ border: "none", background: "transparent", padding: 0, cursor: i === 0 ? "default" : "pointer", color: i === 0 ? T.n5 : T.n2, display: "inline-flex" }}><Icon.arrow dir="up" size={11} /></button>
                            <button type="button" disabled={i === rows.length - 1} onClick={() => store.reorderPackage(p.id, "down")} title={t("services.screens.moveDown", "Move down")} style={{ border: "none", background: "transparent", padding: 0, cursor: i === rows.length - 1 ? "default" : "pointer", color: i === rows.length - 1 ? T.n5 : T.n2, display: "inline-flex" }}><Icon.arrow dir="down" size={11} /></button>
                          </div>
                        ) : null}
                        <span style={{ width: 30, height: 30, borderRadius: 7, background: `linear-gradient(135deg, ${p.hue}, ${T.p1})`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.white, flexShrink: 0 }}><Icon.package size={15} /></span>
                        <div><div style={{ color: T.n1, fontWeight: 600 }}>{p.name}</div><div style={{ color: T.soft, fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>{p.description}</div></div>
                      </div>
                    </td>
                    <td style={svcTd}>{itemSummary(p)}</td>
                    <td style={{ ...svcTd, color: T.n1, fontWeight: 600 }}>{store.packageServiceCount(p)}</td>
                    <td style={svcTd}>{svcStatusPill(p.status)}</td>
                    <td style={{ ...svcTd, fontSize: 12 }}>{p.updated}</td>
                    <td style={svcTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(p)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Screen>
  );
}
